
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import ChecklistItem from '../settlement/ChecklistItem';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import {
  loadResidencePanelCompletion,
  withEstatePath,
} from '../../utils/secureResidenceFlow';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';


// Standardized Logic & Types
import { documentService } from '../../services/documentService';
import {
  listSettlementDocuments,
  uploadSettlementDocument,
  deleteSettlementDocument,
} from '../../utils/settlementDocuments';
import type { AppDocument } from '../../types/document';


interface FrameDataWithNotes {
  completed?: boolean;
  notes?: string;
}


const PHASE = 'iad';
const STEP_ID = 'iad-1';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;


const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';


const DEATH_CERT_PANELS = [
  {
    frameId: 'frame-dc-order',
    title: 'Estimate and Order Certificates',
    intro: 'Determine requirements and place your request.'
  },
  {
    frameId: 'frame-dc-verify',
    title: 'Receive and Verify Certificates',
    intro: 'Check delivery and accuracy of all copies.'
  },
  {
    frameId: 'frame-dc-store',
    title: 'Store and Organize Certificates',
    intro: 'Securely store copies for upcoming steps.'
  },
];


const CHECKLIST_GUIDANCE = [
  {
    what: 'Determine how many certified copies you need and request them from the appropriate source.',
    why: 'Ordering too few can delay the process. Ordering too many can create unnecessary cost.',
    actions: [
      'Plan for 10–15 copies for most estates',
      'Order through funeral home (typically easiest)',
      'Or order via state/county vital records',
      'Confirm number of copies ordered'
    ],
  },
  {
    what: 'Confirm delivery and accuracy of all certified copies.',
    why: 'Ensures you have valid documents ready before moving into legal and financial steps.',
    actions: [
      'Confirm total number received',
      'Check for errors (name, dates, spelling)',
      'Retain receipts or confirmations',
      'Upload copies for reference (optional)'
    ],
  },
  {
    what: 'Securely store and prepare certificates for use in upcoming steps.',
    why: 'Keeping certificates organized ensures quick access when institutions require them.',
    actions: [
      'Store physical copies in a secure location',
      'Keep copies easily accessible',
      'Track how many copies are available',
      'Prepare to use them in upcoming steps (banks, insurance, government, probate)'
    ],
  },
];


export default function SFrame124() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();


  const {
    error,
    loadFrameData,
    loadPhaseProgress,
    updateStepProgress,
    saveFrameData
  } = useSettlementSession(estateId);


  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [notes, setNotes] = useState('');


  const refresh = useCallback(async () => {
    if (!estateId) return;


    // Load checklist completion
    const { byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    const dcFrameIds = DEATH_CERT_PANELS.map(p => p.frameId);
    const dcCount = Object.keys(byFrameId).filter(
      id => dcFrameIds.includes(id) && byFrameId[id]
    ).length;


    setCompletedPanels(dcCount);
    setPanelDone(byFrameId);


    // Load documents using standard normalization
    const rows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
    setDocuments(documentService.normalizeMany(rows));


    // Load notes
    const frameData = await loadFrameData(STEP_ID) as FrameDataWithNotes;
    if (frameData?.notes) setNotes(frameData.notes);
  }, [estateId, loadFrameData]);


  useEffect(() => {
    refresh();
  }, [refresh]);


  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    const newPanelDone = { ...panelDone, [frameId]: newState };
    setPanelDone(newPanelDone);


    const dcFrameIds = DEATH_CERT_PANELS.map(p => p.frameId);
    setCompletedPanels(
      Object.keys(newPanelDone).filter(id => dcFrameIds.includes(id) && newPanelDone[id]).length
    );


    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();


      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };


  /**
   * Optimized Upload Handler
   * Standardized for multiple files and Optimistic UI
   */
  const handleDocUpload = async (files: File[]) => {
    if (!files.length) return;


    // 1. OPTIMISTIC UPDATE: Show cards instantly
    const tempDocs = files.map(file => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      name: file.name,
      type: file.type,
      size: file.size,
    }));


    const normalizedTemp = documentService.normalizeMany(tempDocs);
    setDocuments(prev => [...prev, ...normalizedTemp]);


    // 2. SERVER PERSISTENCE
    if (estateId) {
      try {
        await Promise.all(
          files.map(file =>
            uploadSettlementDocument({
              estateId,
              phase: PHASE,
              stepId: STEP_ID,
              file,
            })
          )
        );


        // Final sync to get server IDs and properties
        const finalRows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
        setDocuments(documentService.normalizeMany(finalRows));


        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      } catch (err) {
        console.error("Backend upload failed", err);
      }
    }
  };


  const handleDocDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to remove this document?')) return;


    setDocuments(prev => prev.filter(d => d.id !== docId));


    if (estateId && !docId.toString().startsWith('temp-')) {
      try {
        await deleteSettlementDocument(estateId, docId);
      } catch (err) {
        console.error("Backend delete failed", err);
      }
    }
  };


  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId) {
      await saveFrameData(STEP_ID, { notes: val } as Record<string, unknown>);
    }
  };


  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
            {error}
          </div>
        )}


        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className={titleClassName}>Obtain Death Certificates</h1>
        </div>


        <p className={subtitleClassName}>
          Order and organize certified copies so you’re prepared for the next steps.
        </p>


        <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-4 mb-8 text-sm text-cyan-900 font-sans">
          <strong>Note:</strong> Having enough certified copies upfront prevents delays across the estate process.
        </div>


        <ProgressHeader
          title="Obtain death certificates"
          variant={isPlanner ? 'planner' : 'default'}
          subtitle="Work through each item as you go. Your progress is saved automatically."
          completedSteps={completedPanels}
          totalSteps={DEATH_CERT_PANELS.length}
        />


        <div className="space-y-4 mt-8">
          {DEATH_CERT_PANELS.map((panel, index) => {
            const guidance = CHECKLIST_GUIDANCE[index];
            return (
              <ChecklistItem
                key={panel.frameId}
                id={panel.frameId}
                title={panel.title}
                description={panel.intro}
                whatThisIs={guidance.what}
                whyItMatters={guidance.why}
                bulletPoints={guidance.actions}
                checked={!!panelDone[panel.frameId]}
                onChange={handleToggleComplete}
              />
            );
          })}
        </div>


        {/* Standardized Documents Section */}
        <div className="mt-12 pt-10 border-t border-stone-200">
          <DocumentUploadSection
            documents={documents}
            onUpload={handleDocUpload}
            onDelete={handleDocDelete}
            className="mb-8"
          />
        </div>


        {/* Standardized Notes Section */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2 font-sans">Internal Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all min-h-[120px] placeholder:text-stone-400"
            placeholder="e.g. Funeral home contact info, date ordered..."
            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

